"use client";

import { Award, Download, Shield, Sparkles, Syringe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Standards() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const standards = [{
    icon: Shield,
    title: "Neutral Environment",
    description: "We maintain a strictly neutral, child-centered space designed to minimize conflict and provide a secure, predictable setting for all family interactions."
  }, {
    icon: Syringe,
    title: "Trauma-Informed Care",
    description: "Our staff is trained in trauma-informed practices, ensuring that every interaction is handled with sensitivity, patience, and an understanding of family dynamics."
  }, {
    icon: Sparkles,
    title: "Objective Documentation",
    description: "We provide detailed, factual, and neutral observation reports for the court, ensuring that all parties have a clear and accurate record of parenting time."
  }, {
    icon: Award,
    title: "Professional Boundaries",
    description: "We uphold firm, clear boundaries to ensure the safety and well-being of children, providing a structured framework that supports healthy family engagement."
  }];
  const certifications = ["TRAUMA-INFORMED", "COURT-ORDERED COMPLIANT", "NEUTRAL REPORTING", "CHILD-CENTERED", "ARIZONA-BASED"];
  return <section data-section-id="32" id="professional-standards" className="py-24 md:py-32 lg:py-40 px-6 md:px-10 font-default dark bg-background text-foreground">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-20 md:mb-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs md:text-sm tracking-widest uppercase mb-8 text-primary">
            § 03 / SAFETY & STANDARDS
          </p>
          <h2 className="font-default text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tight">
            Grounded
            <br />
            <span className="italic">Professionalism.</span>
          </h2>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 relative border-t border-border">
          {standards.map((item, i) => {
          const Icon = item.icon;
          return <div data-index={i} key={i} className={`relative p-8 md:p-10 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${i % 2 === 1 ? "sm:border-l border-border" : ""} ${i >= 2 ? "sm:border-t lg:border-t-0 border-border" : ""} ${i > 0 ? "lg:border-l border-t sm:border-t-0 border-border" : ""}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <Icon className="w-16 h-16 mb-8 text-primary" strokeWidth={1} />
                <h3 className="font-default text-2xl font-medium leading-tight mb-4">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed font-default text-muted-foreground">
                  {item.description}
                </p>
              </div>;
        })}
        </div>

        {}
        <div ref={footerRef} className={`mt-24 md:mt-32 transition-all duration-700 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 mb-16 md:mb-20">
            {certifications.map((cert, i) => <span data-index={i} key={i} className="font-mono text-xs tracking-widest uppercase text-muted-foreground">
                {cert}
              </span>)}
          </div>

          {}
          <div className="flex justify-center">
            <Link to="/ResourcesPage">
              <Button variant="outline" size="lg" className="font-mono text-xs tracking-widest uppercase bg-primary text-white px-8 py-6 h-auto transition-all duration-300 hover:shadow-lg cursor-pointer">
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Download Service Guidelines (PDF)
                <span aria-hidden="true" className="ml-2">→</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}